import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import '../styles/pages/Players.scss';
import '../styles/components/index.scss';
import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupsIcon from '@mui/icons-material/Groups';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import playerMockup1 from '../images/mockups/for-the-players1.png';
import playerMockup2 from '../images/mockups/for-the-players2.png';
import playerMockup3 from '../images/mockups/for-the-players3.png';
import { useJoinModal } from '../components/JoinModalProvider';

// Custom arrow components for the slider
const PrevArrow = (props) => {
  const { className, onClick } = props;
  return (
    <div className={`${className} custom-arrow custom-prev-arrow`} onClick={onClick}>
      <ArrowBackIosNewIcon />
    </div>
  );
};

const NextArrow = (props) => {
  const { className, onClick } = props;
  return (
    <div className={`${className} custom-arrow custom-next-arrow`} onClick={onClick}>
      <ArrowForwardIosIcon />
    </div>
  );
};

const Players = () => {
  const { openModal } = useJoinModal();
  // Smooth scroll function
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Create ref for the player slider
  const playerSliderRef = (slider) => {
    if (slider !== null) {
      window.playerSlider = slider;
    }
  };
  
  // Phone carousel settings
  const playerSliderSettings = {
    dots: true,
    infinite: true,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    arrows: true,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    cssEase: 'ease-out',
    swipe: true,
    swipeToSlide: true,
    touchMove: true,
    touchThreshold: 20,
    waitForAnimate: false
  };

  // Add touch handling for carousels
  useEffect(() => {
    // Ensure slick sliders are properly initialized
    const carousels = document.querySelectorAll('.phone-carousel');
    
    carousels.forEach(carousel => {
      // Make images non-draggable to prevent interference with swipe
      const images = carousel.querySelectorAll('img');
      images.forEach(img => {
        img.draggable = false;
      });
      
      // Force slider to refresh to pick up touch events
      const slider = carousel.querySelector('.slick-slider');
      if (slider) {
        // Force refresh slick to apply touch events
        setTimeout(() => {
          // This will trigger a DOM reflow
          slider.style.display = 'none';
          // Force reflow - properly access offsetHeight
          void slider.offsetHeight;
          slider.style.display = '';
        }, 100);
      }
    });
  }, []);

  // State for FAQ accordion
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    if (expandedFaq === index) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(index);
    }
  };

  // FAQ data
  const faqs = [
    {
      question: "How does FirstWhistle help me stay updated on matches?",
      answer: "FirstWhistle sends automatic notifications about upcoming matches, cancellations, and venue changes directly to your phone. You'll receive all details like location, time, and kit requirements, ensuring you're always prepared."
    },
    {
      question: "Can I track my performance and development?",
      answer: "Yes! You can view your match statistics, skills progress, awards, and coach feedback all in one place. The app tracks your development over the season so you can see your improvement in real-time."
    },
    {
      question: "How do team payments work?",
      answer: "FirstWhistle simplifies team payments with digital tracking for match fees, subscriptions, and team events. For academies, payment is either through their current method or via our partnership with Online Ticket Seller."
    },
    {
      question: "Is my data secure?",
      answer: "Security is our priority. All personal information is encrypted, and we never share data with third parties. Only authorised team officials and you have access to your profile information."
    }
  ];

  // To ensure correct page title
  useEffect(() => {
    document.title = "FirstWhistle - Players";
  }, []);

  return (
    <div className="players-page">
      <Helmet>
        <title>FirstWhistle for Players | Track Your Football Progress & Achievements</title>
        <meta name="description" content="FirstWhistle helps players track their stats, achievements and match details. Never miss a game, showcase your skills, and take your football journey to the next level." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://firstwhistle.app/players" />
        <meta property="og:title" content="FirstWhistle for Players | Track Your Football Progress & Achievements" />
        <meta property="og:description" content="FirstWhistle helps players track their stats, achievements and match details. Never miss a game, showcase your skills, and take your football journey to the next level." />
        <meta property="og:image" content="https://firstwhistle.app/images/players-hero-bg.jpg" />
        <meta property="og:site_name" content="FirstWhistle" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://firstwhistle.app/players" />
        <meta name="twitter:title" content="FirstWhistle for Players | Track Your Football Progress & Achievements" />
        <meta name="twitter:description" content="FirstWhistle helps players track their stats, achievements and match details. Never miss a game, showcase your skills, and take your football journey to the next level." />
        <meta name="twitter:image" content="https://firstwhistle.app/images/players-hero-bg.jpg" />
        
        <link rel="canonical" href="https://firstwhistle.app/players" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "FirstWhistle for Players",
              "description": "FirstWhistle helps players track their stats, achievements and match details. Never miss a game, showcase your skills, and take your football journey to the next level.",
              "mainEntity": {
                "@type": "SoftwareApplication",
                "name": "FirstWhistle",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "iOS, Android",
                "description": "AI-powered football team management application for players focused on performance tracking and team communication.",
                "offers": {
                  "@type": "Offer",
                  "availability": "PreOrder",
                  "price": "0",
                  "priceCurrency": "GBP"
                },
                "audience": {
                  "@type": "Audience",
                  "audienceType": "Football Players",
                  "geographicArea": {
                    "@type": "AdministrativeArea",
                    "name": "United Kingdom"
                  }
                },
                "potentialAction": {
                  "@type": "JoinAction",
                  "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://firstwhistle.app/players",
                    "actionPlatform": [
                      "http://schema.org/DesktopWebPlatform",
                      "http://schema.org/MobileWebPlatform"
                    ]
                  },
                  "name": "Sign Up"
                },
                "featureList": [
                  "Performance stat tracking",
                  "Achievement recording",
                  "Match notifications",
                  "Team events calendar",
                  "Skill development tracking",
                  "Coach feedback interface"
                ]
              }
            }
          `}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section players-hero-section">
        <div className="hero-content players-hero-content">
          <h1 className="players-page-h1"><span className="page-identifier">Players:</span> Take Your Football Journey to the Next Level</h1>
          <p className="subtext players-page-subtext">FirstWhistle connects you to every match, milestone and achievement in your football development.</p>
          <div className="cta-buttons">
            <button className="cta-button primary" onClick={openModal}>Join Now</button>
            <button 
              className="cta-button secondary"
              onClick={() => scrollToSection('how-it-works')}
            >
              See How It Works
            </button>
          </div>
        </div>
        <div className="hero-background">
          <img src={require('../images/players-hero-bg.jpg')} alt="Football Player" className="players-hero-image" />
          <div className="overlay"></div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <h2>Benefits of FirstWhistle</h2>
        <div className="benefits-container">
          <div className="benefit-card">
            <div className="benefit-icon">
              <NotificationsActiveIcon />
            </div>
            <h3>Never Miss a Match</h3>
            <p>Match alerts, directions to venues, and last-minute changes delivered straight to your phone.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">
              <EmojiEventsIcon />
            </div>
            <h3>Track Your Achievements</h3>
            <p>Record goals, assists, and awards. Get selected for team of the week and share highlights with teammates.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">
              <GroupsIcon />
            </div>
            <h3>Be Part of the Team</h3>
            <p>Connect with coaches and teammates through team updates, events and shared moments.</p>
          </div>
        </div>
      </section>

      {/* Game Types Section */}
      <section className="game-types-section">
        <div className="game-types-container">
          <h2>Game Types We Cover</h2>
          <p className="section-description">Whether you're playing in competitive leagues or friendly matches, FirstWhistle has you covered with tailored features for each game type.</p>
          
          <div className="game-types-grid">
            {/* Weekend League Games */}
            <div className="game-type-card">
              <div className="game-type-header">
                <h3>Weekend League Games</h3>
                <p className="game-type-subtitle">Saturday & Sunday Leagues</p>
              </div>
              <div className="game-type-features">
                <ul>
                  <li>
                    <CheckCircleOutlineIcon />
                    <span>Player Data & Statistics</span>
                  </li>
                  <li>
                    <CheckCircleOutlineIcon />
                    <span>AI-Powered Team Selection</span>
                  </li>
                  <li>
                    <CheckCircleOutlineIcon />
                    <span>Detailed Match Reports</span>
                  </li>
                  <li>
                    <CheckCircleOutlineIcon />
                    <span>Player Availability Tracking</span>
                  </li>
                  <li>
                    <CheckCircleOutlineIcon />
                    <span>Training Schedule Management</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Weekly Friendlies */}
            <div className="game-type-card">
              <div className="game-type-header">
                <h3>Weekly Friendlies</h3>
                <p className="game-type-subtitle">Mid-Week Matches</p>
              </div>
              <div className="game-type-features">
                <ul>
                  <li>
                    <CheckCircleOutlineIcon />
                    <span>Player Availability</span>
                  </li>
                  <li>
                    <CheckCircleOutlineIcon />
                    <span>Smart Team Sorting using Ai our own intelligent form calculations.</span>
                  </li>
                  <li>
                    <CheckCircleOutlineIcon />
                    <span>Player Achievements</span>
                  </li>
                  <li>
                    <CheckCircleOutlineIcon />
                    <span>Golden Boot Leaderboard</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="screenshots-section" id="how-it-works">
        <h2>See the app in action</h2>
        <div className="audience-section">
          <div className="phone-carousel">
            <Slider ref={playerSliderRef} {...playerSliderSettings} key="player-slider">
              <div className="phone-slide">
                <img src={playerMockup1} alt="FirstWhistle for Players - Dashboard" className="audience-phone" draggable="false" />
              </div>
              <div className="phone-slide">
                <img src={playerMockup2} alt="FirstWhistle for Players - Stats" className="audience-phone" draggable="false" />
              </div>
              <div className="phone-slide">
                <img src={playerMockup3} alt="FirstWhistle for Players - Awards" className="audience-phone" draggable="false" />
              </div>
            </Slider>
          </div>
          <div className="screenshots-content">
            <h3>For <span style={{ color: '#ffd700' }}>Players</span> Like You</h3>
            <p>Track your stats, stay in the loop, and earn your place. FirstWhistle gives you a clear view of your progress – and the recognition you deserve.</p>
            <ul className="feature-list">
              <li><CheckCircleOutlineIcon className="check-icon" /> Match notifications</li>
              <li><CheckCircleOutlineIcon className="check-icon" /> Player stats and rankings</li>
              <li><CheckCircleOutlineIcon className="check-icon" /> Team selections powered by AI</li>
              <li><CheckCircleOutlineIcon className="check-icon" /> MOTM voting and rewards</li>
              <li><CheckCircleOutlineIcon className="check-icon" /> <em style={{ fontStyle: 'italic', color: '#ffd700' }}>Plus 100s more features</em></li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-container">
          {faqs.map((faq, index) => (
            <div className="faq-item" key={index}>
              <div 
                className="faq-question" 
                onClick={() => toggleFaq(index)}
              >
                <h3>{faq.question}</h3>
                {expandedFaq === index ? <ExpandLessIcon className="expand-icon" /> : <ExpandMoreIcon className="expand-icon" />}
              </div>
              <div className={`faq-answer ${expandedFaq === index ? 'expanded' : ''}`}>
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="social-proof-section">
        <h2>What players are saying about FirstWhistle</h2>
        <div className="testimonials">
          <div className="testimonial">
            <FormatQuoteIcon className="quote-icon" />
            <p>"FirstWhistle has revolutionised our Sunday league. I can track my performance stats and the team's progress throughout the season, which keeps me motivated."</p>
            <div className="author">- James, Sunday League Striker</div>
          </div>
          <div className="testimonial">
            <FormatQuoteIcon className="quote-icon" />
            <p>"As someone juggling work and football, the match notifications and venue directions have been a game-changer. I've not missed a single match since using the app."</p>
            <div className="author">- Sarah, Women's 5-a-side Captain</div>
          </div>
          <div className="testimonial">
            <FormatQuoteIcon className="quote-icon" />
            <p>"The performance tracking has helped me improve areas of my game I didn't even realise needed work. Being recognised for achievements has made playing even more rewarding."</p>
            <div className="author">- Mike, Amateur League Midfielder</div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <h2>Take your football to the next level.</h2>
        <p>Be the first to experience FirstWhistle when we launch and transform how you manage your football team.</p>
        <div className="cta-buttons">
          <button className="cta-button primary" onClick={openModal}>Join Now</button>
          <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer" className="cta-button secondary">
            <InstagramIcon /> Follow Us on Instagram
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <p className="tagline">FirstWhistle – Where every player counts.</p>
          </div>
          <div className="footer-navigation">
            <div className="footer-links">
              <Link to="/about">About</Link>
              <Link to="/players">Players</Link>
              <Link to="/coaches">Coaches</Link>
              <Link to="/parents">Parents</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div className="footer-socials">
              <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer">
                <InstagramIcon className="social-icon" />
              </a>
              <a href="https://www.facebook.com/FirstWhistleApp" target="_blank" rel="noopener noreferrer">
                <FacebookIcon className="social-icon" />
              </a>
            </div>
          </div>
          <div className="footer-legal">
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Players; 